import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Phone, Mail, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { HOURS } from "../components/Preise";

const CARDS = [
  {
    icon: Phone,
    title: "Anrufen",
    value: "0176 32526128",
    href: "tel:+4917632526128",
    note: "Reservieren & kurzfristig fragen — besonders am Wochenende: einfach mal anrufen.",
    testid: "kontakt-card-phone",
  },
  {
    icon: Mail,
    title: "Schreiben",
    value: "vintagebillard@gmail.com",
    href: "mailto:vintagebillard@gmail.com",
    note: "Für Anfragen, Gruppen und alles, was nicht eilt.",
    testid: "kontakt-card-mail",
  },
  {
    icon: MapPin,
    title: "Vorbeikommen",
    value: "Hemelinger Heerstraße 7, 28309 Bremen",
    href: "https://www.google.com/maps/dir/?api=1&destination=Vintage+Billard,+Hemelinger+Heerstra%C3%9Fe+7,+28309+Bremen",
    note: "Buslinien 40 & 41 um die Ecke, Parkplätze vorhanden — ein Tisch findet sich immer.",
    testid: "kontakt-card-visit",
  },
];

export default function Kontakt() {
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" data-testid="kontakt-page">
      <header className="border-b border-[#241a15]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <Link to="/" data-testid="kontakt-logo-link" className="flex items-center gap-3">
            <img
              src="/images/logo.png"
              alt="Vintage Billard Logo"
              className="h-10 w-10 rounded-full border border-[#c9a24a]/40 object-cover"
            />
            <span className="font-display text-lg text-[#f4ecdd]">Vintage Billard</span>
          </Link>
          <Link
            to="/"
            data-testid="kontakt-back-link"
            className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#c9bda9] transition-colors duration-300 hover:text-[#e3c988]"
          >
            <ArrowLeft size={14} /> Zurück zur Startseite
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.22em] text-[#c9a24a]">Kontakt</p>
          <h1 className="mt-4 font-display text-4xl font-light text-[#f4ecdd] md:text-6xl">
            Sag einfach <span className="italic text-[#e3c988]">Bescheid.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#c9bda9] md:text-lg">
            Ob Tisch reservieren, Geburtstag planen oder kurz fragen, ob was frei ist — wir freuen
            uns von dir zu hören.
          </p>
        </motion.div>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {CARDS.map(({ icon: Icon, title, value, href, note, testid }, i) => (
            <motion.a
              key={title}
              href={href}
              target={href.startsWith("http") ? "_blank" : undefined}
              rel={href.startsWith("http") ? "noreferrer" : undefined}
              data-testid={testid}
              className="group flex flex-col border border-[#241a15] bg-[#1c1512] p-8 transition-colors duration-500 hover:bg-[#241a15]"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
            >
              <Icon size={28} className="text-[#c9a24a] transition-transform duration-500 group-hover:-translate-y-1" />
              <h2 className="mt-8 font-display text-2xl text-[#f4ecdd] group-hover:text-[#e3c988]">{title}</h2>
              <p className="mt-3 font-display text-lg text-[#e3c988]">{value}</p>
              <p className="mt-4 text-sm leading-relaxed text-[#c9bda9]">{note}</p>
            </motion.a>
          ))}
        </div>

        <motion.div
          className="mt-16 grid gap-8 lg:grid-cols-2"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative border border-[#c9a24a]/25 p-8 md:p-10" data-testid="kontakt-hours">
            <div className="pointer-events-none absolute inset-2 border border-[#c9a24a]/15" />
            <p className="text-xs uppercase tracking-[0.22em] text-[#c9a24a]">Öffnungszeiten</p>
            <div className="mt-6">
              {HOURS.map(([day, time]) => (
                <div key={day} className="flex items-end gap-4 py-2.5">
                  <span className="text-[#f4ecdd]">{day}</span>
                  <span className="mb-1.5 flex-1 border-b border-dotted border-[#c9bda9]/30" />
                  <span className={`whitespace-nowrap font-display ${time === "geschlossen" ? "italic text-[#c9bda9]/70" : "text-[#e3c988]"}`}>
                    {time}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <p className="font-display text-2xl italic leading-relaxed text-[#e3c988] md:text-3xl" data-testid="kontakt-dropin">
              „Oder komm einfach mal vorbei — ein Tisch findet sich immer."
            </p>
            <p className="mt-6 max-w-md text-base leading-relaxed text-[#c9bda9]">
              Mitten in Hemelingen, nur wenige Gehminuten von den Buslinien 40 & 41 entfernt.
              Wir freuen uns auf dich.
            </p>
            <Link
              to="/"
              data-testid="kontakt-home-cta"
              className="mt-10 inline-flex w-fit border border-[#c9a24a] px-8 py-4 text-sm uppercase tracking-[0.18em] text-[#c9a24a] transition-colors duration-300 hover:bg-[#c9a24a] hover:text-[#120d0b]"
            >
              Zurück zur Startseite
            </Link>
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-[#241a15] px-6 py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-[#c9bda9]/60">
          <Link to="/" className="transition-colors duration-300 hover:text-[#e3c988]">Startseite</Link>
          <Link to="/impressum" className="transition-colors duration-300 hover:text-[#e3c988]">Impressum</Link>
          <Link to="/datenschutz" className="transition-colors duration-300 hover:text-[#e3c988]">Datenschutz</Link>
          <span className="normal-case tracking-normal">© 2026 Vintage Billard</span>
        </div>
      </footer>
    </div>
  );
}
